// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback } from "react";
import { Plus, SquarePen, Trash2, Loader2, X } from "lucide-react";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import SortableHeader from "../../components/admin/SortableHeader";
import PageLoader from "../../components/PageLoader";
import tenantService from "../../services/tenantService";
import { formatErrorMessage } from "../../utils/apiHelper";
import useSortableData from "../../hooks/useSortableData";

const DEFAULT_PAGE_SIZE = 25;

// Only "Status" needs a custom sort value (the is_active boolean); every other
// column maps directly to a row field.
const PLAN_SORT_ACCESSORS = {
  status: (p) => (p.is_active ? 1 : 0),
};

/** Map list API rows (plan_id, plan_name, …) to a stable in-memory shape. */
const normalizePlanFromApi = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.plan_id ?? raw.id;
  if (id == null) return null;
  const yearRaw = raw.price_eur_year ?? raw.annual_price;
  const annualNum =
    yearRaw != null && yearRaw !== "" ? Number(yearRaw) : Number.NaN;
  return {
    id: String(id),
    plan_code: raw.plan_code ?? raw.code ?? "",
    name: raw.plan_name ?? raw.name ?? "",
    description: raw.description ?? "",
    monthly_price: raw.monthly_price != null ? Number(raw.monthly_price) : null,
    annual_price: Number.isFinite(annualNum) ? annualNum : null,
    display_order: Number(raw.display_order) || 0,
    is_active: Boolean(raw.is_active),
    features: raw.features ?? null,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
};

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [formData, setFormData] = useState({
    plan_code: "",
    name: "",
    description: "",
    price_eur_year: "",
    display_order: "0",
    is_active: true,
    features_json: "",
  });

  const parseFeatures = (value) => {
    if (!value?.trim()) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const stringifyFeatures = (value) => {
    if (!value) return "";
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return "";
    }
  };

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tenantService.getPlans({
        page,
        limit,
      });
      const items =
        data?.items ?? data?.plans ?? data?.data ?? data?.results ?? [];
      const totalCount =
        data?.total ?? data?.count ?? (Array.isArray(items) ? items.length : 0);
      const rawList = Array.isArray(items) ? items : [];
      const normalized = rawList
        .map(normalizePlanFromApi)
        .filter(Boolean)
        .sort(
          (a, b) =>
            a.display_order - b.display_order ||
            a.name.localeCompare(b.name, "de"),
        );
      setPlans(normalized);
      setTotal(Number(totalCount) || 0);
      const tp = Number(data?.total_pages);
      setServerTotalPages(Number.isFinite(tp) && tp > 0 ? tp : null);
    } catch (err) {
      setError(
        formatErrorMessage(err) || "Pläne konnten nicht geladen werden.",
      );
      setPlans([]);
      setTotal(0);
      setServerTotalPages(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openCreate = () => {
    setActionError(null);
    setFormData({
      plan_code: "",
      name: "",
      description: "",
      price_eur_year: "",
      display_order: "0",
      is_active: true,
      features_json: "",
    });
    setEditingPlan(null);
    setShowCreateModal(true);
  };

  const openEdit = (plan) => {
    setActionError(null);
    setEditingPlan(plan);
    setFormData({
      plan_code: plan.plan_code ?? "",
      name: plan.name ?? "",
      description: plan.description ?? "",
      price_eur_year:
        plan.annual_price != null ? String(plan.annual_price) : "",
      display_order:
        plan.display_order != null ? String(plan.display_order) : "0",
      is_active: plan.is_active ?? true,
      features_json: stringifyFeatures(plan.features),
    });
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      plan_code: "",
      name: "",
      description: "",
      price_eur_year: "",
      display_order: "0",
      is_active: true,
      features_json: "",
    });
  };

  const buildPlanPayload = () => {
    const features = parseFeatures(formData.features_json);
    const displayOrderParsed = parseInt(String(formData.display_order), 10);
    const payload = {
      plan_code: formData.plan_code?.trim() || null,
      plan_name: formData.name?.trim(),
      description: formData.description?.trim() || null,
      price_eur_year: formData.price_eur_year
        ? parseFloat(formData.price_eur_year)
        : null,
      display_order: Number.isFinite(displayOrderParsed)
        ? displayOrderParsed
        : 0,
      is_active: Boolean(formData.is_active),
    };
    if (features !== null) payload.features = features;
    return payload;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !formData.plan_code?.trim()) return;
    setSaving(true);
    setActionError(null);

    const payload = buildPlanPayload();

    try {
      await tenantService.createPlan(payload);
      await fetchPlans();
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !formData.plan_code?.trim() || !editingPlan)
      return;
    setSaving(true);
    setActionError(null);

    const payload = buildPlanPayload();

    try {
      await tenantService.updatePlan(editingPlan.id, payload);
      await fetchPlans();
      setEditingPlan(null);
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (plan) => {
    setActionError(null);
    setDeleteConfirm(plan);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setActionError(null);
    try {
      await tenantService.deletePlan(deleteConfirm.id);
      await fetchPlans();
      setDeleteConfirm(null);
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const totalPages =
    serverTotalPages != null
      ? Math.max(1, serverTotalPages)
      : Math.max(1, Math.ceil(total / limit));

  // Client-side sort of the loaded page; falls back to the fetch order
  // (display_order, then name) while no header is active.
  const {
    sortedItems: sortedPlans,
    sortConfig,
    requestSort,
  } = useSortableData(plans, PLAN_SORT_ACCESSORS);

  return (
    <div className="relative">
      <GlobalAdminHeader
        title="Pläne"
        subtitle="Abonnements- und Preispläne verwalten"
        buttons={[
          {
            text: "Neuer Plan",
            icon: <Plus className="h-4 w-4" />,
            onClick: openCreate,
            className:
              "flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200">
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Code"
                    sortKey="plan_code"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Beschreibung"
                    sortKey="description"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Preis (€/Jahr)"
                    sortKey="annual_price"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Sortierung"
                    sortKey="display_order"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      <PageLoader message="Lade Pläne..." />
                    </td>
                  </tr>
                ) : plans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      Keine Pläne gefunden.
                    </td>
                  </tr>
                ) : (
                  sortedPlans.map((plan) => (
                    <tr
                      key={plan.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-lg font-semibold text-gray-900 dark:text-slate-100 truncate max-w-[12rem]">
                        {plan.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 font-mono truncate max-w-[8rem]">
                        {plan.plan_code?.trim() || "—"}
                      </td>
                      <td
                        className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 max-w-xs truncate"
                        title={plan.description || undefined}
                      >
                        {plan.description?.trim() || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 font-mono whitespace-nowrap">
                        {plan.annual_price != null
                          ? `${Number(plan.annual_price).toFixed(2)} €`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 tabular-nums">
                        {plan.display_order}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            plan.is_active
                              ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                              : "bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-300"
                          }`}
                        >
                          {plan.is_active ? "Aktiv" : "Inaktiv"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(plan)}
                            className="p-2 rounded-lg hover:bg-primary-color-tint primary-color transition-colors"
                            title="Bearbeiten"
                          >
                            <SquarePen className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(plan)}
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 text-sm">
              <span className="text-gray-600 dark:text-slate-400">
                {total} Plan{total !== 1 ? "e" : ""}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>
                <span className="text-gray-700 dark:text-slate-300">
                  Seite {page} von {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      {(showCreateModal || editingPlan) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() =>
              !saving &&
              (showCreateModal
                ? setShowCreateModal(false)
                : setEditingPlan(null))
            }
            aria-hidden
          />
          <div
            className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {editingPlan ? "Plan bearbeiten" : "Neuer Plan"}
              </h2>
              <button
                type="button"
                onClick={() =>
                  showCreateModal
                    ? setShowCreateModal(false)
                    : setEditingPlan(null)
                }
                disabled={saving}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-slate-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={editingPlan ? handleEditSubmit : handleCreateSubmit}
              className="p-6 space-y-4"
            >
              {actionError && (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {actionError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Plan-Code *
                </label>
                <input
                  type="text"
                  value={formData.plan_code}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      plan_code: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="z. B. professional"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Anzeigename *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Preis (€ / Jahr)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price_eur_year}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        price_eur_year: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Sortierung
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        display_order: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="plan-active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, is_active: e.target.checked }))
                  }
                  className="rounded border-gray-300 dark:border-slate-500 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor="plan-active"
                  className="text-sm text-gray-700 dark:text-slate-300"
                >
                  Plan ist aktiv
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Features (JSON)
                </label>
                <textarea
                  value={formData.features_json}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      features_json: e.target.value,
                    }))
                  }
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder='z. B. { "max_users": 10, "api_access": true }'
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() =>
                    showCreateModal
                      ? setShowCreateModal(false)
                      : setEditingPlan(null)
                  }
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingPlan ? "Speichern" : "Anlegen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setDeleteConfirm(null)}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:border dark:border-slate-700 max-w-md w-full p-6">
            {actionError && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {actionError}
              </div>
            )}
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
                  Plan löschen?
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
                  „{deleteConfirm.name ?? "Unbekannt"}“ wird dauerhaft gelöscht.
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 dark:bg-red-500 text-white font-medium hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansManagement;
